'use client';

import { Box } from '@mui/material';
import { useMemo } from 'react';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { StrapiImage } from '@/components/Utility/StrapiImage';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import {
  ComponentBlocksInfoTilesBlock,
  ComponentBlocksInfoTilesRow,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types';

interface InfoTileProps {
  tile: {
    TileSize?: any;
    TileMedia?: any;
    TileContents?: any;
  };
}

function InfoTile({ tile }: InfoTileProps) {
  return (
    <Box
      sx={(_theme) => ({
        minWidth: tile?.TileSize === 'large' ? `25rem` : `15rem`,
        width: '100%',
        gridColumn: `span ${tile?.TileSize === 'large' ? 2 : 1}`,
        gridRow: `span ${tile?.TileSize === 'large' ? 2 : 1}`,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      })}
    >
      {tile.TileMedia && (
        <StrapiImage
          image={tile.TileMedia}
          size="large"
          width={tile.TileMedia?.formats?.large?.width}
          height={tile.TileMedia?.formats?.large?.height}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}

      {tile.TileContents && (
        <Box px={1.5} py={1.5} textAlign="center">
          <GlobalBlockRenderer content={tile.TileContents} textColor="inherit" />
        </Box>
      )}
    </Box>
  );
}

const GRID_COLUMN_CANDIDATES = [5, 4];

function isHangingRowAcceptable(cols: number, remainder: number) {
  if (remainder === 0) return true;
  if (remainder === 1) return false;
  if (remainder === 2) return cols >= 5;
  return true;
}

function getGridColumns(length: number) {
  if (!length || length < 2) {
    return 1;
  }

  const smallestCandidate = GRID_COLUMN_CANDIDATES[GRID_COLUMN_CANDIDATES.length - 1];
  if (length < smallestCandidate) {
    // Fewer tiles than the narrowest grid: one row, no hanging row to worry about.
    return length;
  }

  const options = GRID_COLUMN_CANDIDATES.map((cols) => ({ cols, remainder: length % cols }));

  const perfectFit = options.find((option) => option.remainder === 0);
  if (perfectFit) {
    return perfectFit.cols;
  }

  const acceptable = options.filter((option) =>
    isHangingRowAcceptable(option.cols, option.remainder)
  );
  if (acceptable.length > 0) {
    return acceptable.reduce((best, option) => (option.remainder > best.remainder ? option : best))
      .cols;
  }

  // Every candidate would leave a single hanging item; prefer 2 hanging items over 1.
  const twoHanging = options.find((option) => option.remainder === 2);
  return twoHanging ? twoHanging.cols : options[0].cols;
}

interface TileRowProps {
  row: ComponentBlocksInfoTilesRow;
  rowIndex: number;
}

function TileRow({ row, rowIndex }: TileRowProps) {
  const cols = useMemo(() => {
    if (row.RowColumns && row.RowColumns !== 'auto') {
      return parseInt(row.RowColumns, 10);
    }
    const length = row.Tiles?.reduce((total, currentValue) => {
      if (currentValue?.TileSize === 'large') {
        return total + 2;
      }
      return total + 1;
    }, 0);
    return getGridColumns(length ?? 0);
  }, [row]);

  return (
    <Box
      sx={(theme) => ({
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1.5),
        mb: theme.spacing(3),
      })}
    >
      {row.Tiles?.filter((tile): tile is NonNullable<typeof tile> => tile != null).map(
        (tile, tileIndex) => (
          <InfoTile key={`tile-${rowIndex}-${tileIndex}`} tile={tile} />
        )
      )}
    </Box>
  );
}

export function InfoTilesBlock({ block }: { block: ComponentBlocksInfoTilesBlock }) {
  return (
    <CustomBackground
      backgroundOptions={(block?.InfoTilesBackground ?? undefined) as StrapiBackgroundOptions}
      displayOptions={
        {
          ...block?.InfoTilesDisplayOptions,
          ContentsWidth:
            block?.InfoTilesDisplayOptions?.ContentsWidth ??
            Enum_Componentshareddisplayoptions_Contentswidth.Wide,
          PaddingTop: 'md',
          PaddingBottom: 'md',
          PaddingInline: 'sm',
        } as StrapiDisplayOptions
      }
      boxProps={{
        sx: {
          'data-component': 'InfoTilesBlock',
        },
      }}
    >
      {block.InfoTilesIntro && (
        <Box
          sx={{
            mb: 3,
            textAlign: 'center',
            'h1,h2,h3,h4,h5,h6': { textAlign: 'center' },
          }}
        >
          <GlobalBlockRenderer content={block.InfoTilesIntro} textColor="inherit" />
        </Box>
      )}

      {block.TileRows && block.TileRows.length > 0 && (
        <Box>
          {block.TileRows.map(
            (row, rowIndex) =>
              row && <TileRow key={`row-${rowIndex}`} row={row} rowIndex={rowIndex} />
          )}
        </Box>
      )}
    </CustomBackground>
  );
}
